import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import HubSelection from "@/components/hub/HubSelection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane, Users, Play, LogOut, RefreshCw, MapPin, Check } from "lucide-react";

export default function GameLobby() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [teamId, setTeamId] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/games`);
      setGames(response.data);
    } catch {
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async (e) => {
    e.preventDefault();
    setJoining(true);

    try {
      await axios.post(`${API}/teams/join`, {
        team_id: teamId,
        join_code: joinCode,
      });
      toast.success("Successfully joined the team!");
      setJoinDialogOpen(false);
      setTeamId("");
      setJoinCode("");
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join team");
    } finally {
      setJoining(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/skyboard-logo.png" alt="SkyBoard" className="h-10 w-auto" />
              <div>
                <h1 className="text-lg font-heading font-bold text-white tracking-wide">SKYBOARD</h1>
                <p className="text-xs text-slate-400">Student Portal</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <span className="text-sm text-slate-300">{user?.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900">{t("lobby.title")}</h2>
            <p className="text-slate-500 mt-1">{t("lobby.selectGame")}</p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchGames}
              className="gap-2"
              data-testid="refresh-games-btn"
            >
              <RefreshCw className="w-4 h-4" />
              {t("common.refresh")}
            </Button>

            <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="join-team-btn">
                  <Users className="w-4 h-4" />
                  {t("lobby.joinTeam")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("lobby.joinTeam")}</DialogTitle>
                  <DialogDescription>
                    Enter the team ID and join code provided by your instructor
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleJoinTeam} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="teamId">Team ID</Label>
                    <Input
                      id="teamId"
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      value={teamId}
                      onChange={(e) => setTeamId(e.target.value)}
                      required
                      data-testid="join-team-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="joinCode">Join Code</Label>
                    <Input
                      id="joinCode"
                      placeholder="ABC123"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      required
                      maxLength={6}
                      data-testid="join-code-input"
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setJoinDialogOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button type="submit" disabled={joining} data-testid="submit-join-btn">
                      {joining ? t("common.loading") : t("lobby.joinTeam")}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-20 bg-slate-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : games.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Games Yet</h3>
              <p className="text-slate-500 max-w-sm mx-auto">
                You haven&apos;t joined any games yet. Click &ldquo;Join Team&rdquo; and enter the credentials
                provided by your instructor to get started.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <GameCard key={game.id} game={game} onRefresh={fetchGames} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function GameCard({ game, onRefresh }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [team, setTeam] = useState(null);
  const [showHubPicker, setShowHubPicker] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchMyTeam();
  }, [game.id]);

  const fetchMyTeam = async () => {
    try {
      const response = await axios.get(`${API}/my-team/${game.id}`);
      setTeam(response.data);
    } catch {
      // Team not yet assigned — expected during setup phase
    }
  };

  const statusColors = {
    setup: "bg-amber-100 text-amber-800",
    active: "bg-emerald-100 text-emerald-800",
    completed: "bg-slate-100 text-slate-800",
  };

  const needsHub = game.status === "setup" && team && !team.hub_id;
  const hasHub = team?.hub_id;

  // Full-screen hub picker overlay
  if (showHubPicker && team) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-50 overflow-auto">
        <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-heading font-bold">{game.name} — {team.name}</h2>
          <Button variant="outline" size="sm" onClick={() => setShowHubPicker(false)}>
            {t("common.cancel")}
          </Button>
        </div>
        <HubSelection
          gameId={game.id}
          teamId={team.id}
          onHubSelected={() => {
            setShowHubPicker(false);
            fetchMyTeam();
            if (onRefresh) onRefresh();
          }}
        />
      </div>
    );
  }

  return (
    <Card className="hover-lift cursor-pointer group" data-testid={`game-card-${game.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="font-heading text-xl">{game.name}</CardTitle>
            <CardDescription className="mt-1">
              {team ? `Team: ${team.name} (${team.airline_code})` : "Loading team..."}
            </CardDescription>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[game.status]}`}>
            {game.status.charAt(0).toUpperCase() + game.status.slice(1)}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{t("history.quarter")}</p>
            <p className="text-lg font-mono font-semibold text-slate-700">
              {game.current_quarter === 0 ? (
                <span className="text-amber-600" data-testid={`quarter-zero-${game.id}`}>{t("hub.decisionZero")}</span>
              ) : (
                `${game.current_quarter} / ${game.quarters_count}`
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Hub</p>
            {hasHub ? (
              <p className="text-lg font-mono font-semibold text-emerald-600 flex items-center gap-1">
                <Check className="w-4 h-4" /> {team.hub_id}
              </p>
            ) : (
              <p className="text-sm text-amber-600 font-medium">{t("hub.notSelected")}</p>
            )}
          </div>
        </div>

        {/* Setup phase: show hub selection button */}
        {needsHub && (
          <Button
            className="w-full gap-2 bg-amber-500 hover:bg-amber-600 mb-2"
            onClick={() => setShowHubPicker(true)}
            data-testid={`select-hub-btn-${game.id}`}
          >
            <MapPin className="w-4 h-4" />
            {t("hub.selectHub")}
          </Button>
        )}

        {/* Setup phase with hub selected: waiting */}
        {game.status === "setup" && hasHub && (
          <Button className="w-full gap-2" disabled data-testid={`waiting-btn-${game.id}`}>
            <RefreshCw className="w-4 h-4 animate-spin" />
            {t("lobby.waitingToStart")}
          </Button>
        )}

        {/* Setup phase without team: waiting */}
        {game.status === "setup" && !team && (
          <Button className="w-full gap-2" disabled>
            {t("lobby.waitingToStart")}
          </Button>
        )}

        {/* Active/completed: enter game */}
        {game.status !== "setup" && (
          <Button
            className="w-full gap-2 group-hover:bg-sky-600 transition-colors"
            onClick={() => navigate(`/game/${game.id}`)}
            data-testid={`enter-game-${game.id}-btn`}
          >
            <Play className="w-4 h-4" />
            {t("lobby.enterGame")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
